import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, TrendingUp, Wallet, Scale, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatCurrency } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const CustomerAccountsSheet = () => {
  const [accountsData, setAccountsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ mtd: 0, payments: 0, balance: 0 });
  const { currencySymbol } = useCurrency();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  
  // Period selection state (Admin only)
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  useEffect(() => {
    fetchAccountsData();
  }, [selectedMonth, selectedYear]);

  const fetchAccountsData = async () => {
    try {
      setLoading(true);
      let url = `${API}/customer-accounts-sheet`;
      if (isAdmin) {
        url += `?month=${selectedMonth}&year=${selectedYear}`;
      }
      const response = await axios.get(url, getAuthHeader());
      setAccountsData(response.data.accounts);
      setTotals(response.data.totals);
    } catch (error) {
      toast.error('Failed to fetch customer accounts data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get selected month name
  const getMonthName = (month, year) => {
    const monthName = MONTHS.find(m => m.value === month)?.label || '';
    return `${monthName} ${year}`;
  };
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    const now = new Date();
    const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear();
    if (isCurrentMonth) return; // Don't go beyond current month
    
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };
  
  // Check if we can go to next month
  const canGoNext = () => {
    const now = new Date();
    return !(selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear());
  };

  // Get current month name (for non-admin)
  const getCurrentMonthName = () => {
    const now = new Date();
    return now.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading customer accounts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-accounts-sheet-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Customer Accounts Sheet
          </h1>
          <p className="text-lg text-slate-600 mt-2">
            Overview of customer orders and payments for {isAdmin ? getMonthName(selectedMonth, selectedYear) : getCurrentMonthName()}
          </p>
        </div>
        
        {/* Period Selector - Admin Only */}
        {isAdmin && (
          <div className="flex items-center gap-2 bg-white border rounded-lg p-2 shadow-sm">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={goToPreviousMonth}
              className="h-8 w-8 p-0"
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-32 h-8" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-24 h-8" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i).map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={goToNextMonth}
              disabled={!canGoNext()}
              className="h-8 w-8 p-0"
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-5 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-100">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Total Customers</p>
              <p className="text-2xl font-bold text-blue-600">{accountsData.length}</p>
            </div>
          </div>
        </div>

        <div className="card p-5 bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-amber-100">
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Total MTD Orders</p>
              <p className="text-2xl font-bold text-amber-600">{currencySymbol}{formatCurrency(totals.mtd)}</p>
            </div>
          </div>
        </div>

        <div className="card p-5 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-100">
              <Wallet className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Total Payments</p>
              <p className="text-2xl font-bold text-emerald-600">{currencySymbol}{formatCurrency(totals.payments)}</p>
            </div>
          </div>
        </div>

        <div className="card p-5 bg-gradient-to-br from-red-50 to-white border-2 border-red-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-red-100">
              <Scale className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase">Total Balance</p>
              <p className="text-2xl font-bold text-red-600">{currencySymbol}{formatCurrency(totals.balance)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Customer Name</th>
                <th className="text-right">Month-to-Date Total</th>
                <th className="text-right">Payments</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody data-testid="accounts-table-body">
              {accountsData.map((account, index) => (
                <tr key={account.customer_id || index}>
                  <td className="font-medium">{account.customer_name}</td>
                  <td className="text-right">
                    <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-md font-semibold">
                      {currencySymbol}{formatCurrency(account.mtd_total || 0)}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md font-semibold">
                      {currencySymbol}{formatCurrency(account.payments || 0)}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className={`px-2 py-1 rounded-md font-semibold ${
                      account.balance > 0 
                        ? 'bg-red-100 text-red-700' 
                        : account.balance < 0 
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-700'
                    }`}>
                      {currencySymbol}{formatCurrency(account.balance || 0)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {accountsData.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No customers found. Add customers from the Customers tab.
            </div>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="card p-4 bg-blue-50 border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> This sheet shows data for {isAdmin ? getMonthName(selectedMonth, selectedYear) : getCurrentMonthName()}. 
          "Month-to-Date Total" is the sum of all order amounts for each customer this month. 
          "Balance" = MTD Total - Payments (positive means customer owes, negative means overpaid).
          {isAdmin && " As Admin, you can navigate to view past months using the period selector above."}
        </p>
      </div>
    </div>
  );
};

export default CustomerAccountsSheet;
